import { Injectable } from "@angular/core";
import { TimeZoneService } from "./timezone.service";
import { GetDateRangeType } from "src/app/models/date-range/get-date-range.models";
import { AccountingPeriod, AccountingPeriodService, AccountingPeriodWithMonths } from "./accounting-period-setting.service";
import { BehaviorSubject, Observable } from "rxjs";
import { HierarchicalPostingPeriod, PostingPeriodListService } from "./posting-period-list.service";
import { UtilityService } from "src/app/inventory/sales-order/utils/utility.service";

@Injectable({ providedIn: 'root' })
export class DateRangeFilterService {
    private accountingPeriodSubject = new BehaviorSubject<AccountingPeriodWithMonths | null>(null);
    public accountingPeriod$ = this.accountingPeriodSubject.asObservable();
    private initializationPromiseForAccountingPeriod: Promise<void>;
    private postingPeriodsSubject = new BehaviorSubject<HierarchicalPostingPeriod[]>(null);
    public postingPeriods$ = this.postingPeriodsSubject.asObservable();
    private initializationPromiseForPostingPeriods: Promise<void>;

    constructor(
        private timeZoneService: TimeZoneService,
        private accountingPeriodService: AccountingPeriodService,
        private postingPeriodListService: PostingPeriodListService,
        private utilityService: UtilityService,
    ) {
        this.initializationPromiseForAccountingPeriod = this.initializeAccountingPeriod();
        this.initializationPromiseForPostingPeriods = this.initializePostingPeriods();
    }

    private async initializeAccountingPeriod(): Promise<void> {
        try {
            const period = await this.accountingPeriodService.fetchAccountingPeriodSetting();
            this.accountingPeriodSubject.next(period);
        } catch (error) {
            this.accountingPeriodSubject.next(null);
        }
    }

    private async initializePostingPeriods(): Promise<void> {
        try {
            const period = await this.postingPeriodListService.fetchPostingPeriods();
            this.postingPeriodsSubject.next(period);
        } catch (error) {
            this.postingPeriodsSubject.next(null);
        }
    }

    async waitForInitializationForAccountingPeriod(): Promise<void> {
        return this.initializationPromiseForAccountingPeriod;
    }

    async waitForInitializationForPostingPeriods(): Promise<void> {
        return this.initializationPromiseForPostingPeriods;
    }

    get accountingPeriod(): AccountingPeriodWithMonths | null {
        return this.accountingPeriodSubject.value;
    }

    get postingPeriods(): HierarchicalPostingPeriod[] {
        return this.postingPeriodsSubject.value;
    }

    changeRange(data, period?: HierarchicalPostingPeriod) {
        let today = this.timeZoneService.showDateTimeWithZone(new Date());
        let todayCopy = this.timeZoneService.showDateTimeWithZone(new Date());
        let code = data.code;
        let dateFrom = null;
        let dateTo = null;
        const { fiscalYearStartDate, fiscalYearEndDate } = this.calculateYearRange(this.accountingPeriod, today);
        const lastFiscalYearStartDate = new Date(fiscalYearStartDate);
        lastFiscalYearStartDate.setFullYear(fiscalYearStartDate.getFullYear() - 1);
        const lastYearEndDate = new Date(fiscalYearEndDate);
        lastYearEndDate.setFullYear(fiscalYearEndDate.getFullYear() - 1);
        if (code == GetDateRangeType.all) {
            dateFrom = null;
            dateTo = null;
        } else if (code == GetDateRangeType.today) {
            dateFrom = today;
            dateTo = today;
        } else if (code == GetDateRangeType.yesterday) {
            let yesterday = todayCopy;
            yesterday.setDate(todayCopy.getDate() - 1);
            dateFrom = yesterday;
            dateTo = yesterday;
        } else if (code == GetDateRangeType.thisMonth) {
            let firstday = todayCopy;
            firstday.setDate(1);
            dateFrom = firstday;
            dateTo = new Date(dateFrom.getFullYear(), dateFrom.getMonth() + 1, 0);
        } else if (code == GetDateRangeType.thisMonthToDate) {
            let firstday = todayCopy;
            firstday.setDate(1);
            dateFrom = firstday;
            dateTo = today;
        } else if (code == GetDateRangeType.lastMonth) {
            dateFrom = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            dateTo = new Date(today.getFullYear(), today.getMonth(), 0);
        } else if (code == GetDateRangeType.lastMonthToDate) {
            dateFrom = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            dateTo = today;
        } else if (code == GetDateRangeType.lastQuarter) {
            const fiscalStartMonth = fiscalYearStartDate.getMonth(); // 0-indexed
            const { dateFrom: from, dateTo: to } = this.getFiscalQuarterRange(today, fiscalStartMonth, false, true);
            dateFrom = from;
            dateTo = to;
        } else if (code == GetDateRangeType.lastQuarterToDate) {
            const fiscalStartMonth = fiscalYearStartDate.getMonth(); // 0-indexed
            const { dateFrom: from, dateTo: to } = this.getFiscalQuarterRange(today, fiscalStartMonth, true, true);
            dateFrom = from;
            dateTo = to;
        } else if (code == GetDateRangeType.last7Days) {
            let seven = todayCopy;
            seven.setDate(todayCopy.getDate() - 7);
            dateFrom = seven;
            dateTo = today;
        } else if (code == GetDateRangeType.last15Days) {
            let fifteen = todayCopy;
            fifteen.setDate(todayCopy.getDate() - 15);
            dateFrom = fifteen;
            dateTo = today;
        } else if (code == GetDateRangeType.last30Days) {
            let thirty = todayCopy;
            thirty.setDate(todayCopy.getDate() - 30);
            dateFrom = thirty;
            dateTo = today;
        } else if (code == GetDateRangeType.last60Days) {
            let sixty = todayCopy;
            sixty.setDate(todayCopy.getDate() - 60);
            dateFrom = sixty;
            dateTo = today;
        } else if (code == GetDateRangeType.last90Days) {
            let ninety = todayCopy;
            ninety.setDate(todayCopy.getDate() - 90);
            dateFrom = ninety;
            dateTo = today;
        } else if (code == GetDateRangeType.thisWeek) {
            dateFrom = new Date(today.setDate(today.getDate() - today.getDay()));
            dateTo = new Date(today.setDate(today.getDate() - today.getDay() + 6));
        } else if (code == GetDateRangeType.thisWeekToDate) {
            dateFrom = new Date(today.setDate(today.getDate() - today.getDay()));
            dateTo = todayCopy;
        } else if (code == GetDateRangeType.lastWeek) {
            dateFrom = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7 - today.getDay());
            dateTo = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1 - today.getDay());
        } else if (code == GetDateRangeType.lastWeekToDate) {
            dateFrom = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7 - today.getDay());
            dateTo = today;
        } else if (code == GetDateRangeType.thisQuarter) {
            const fiscalStartMonth = fiscalYearStartDate.getMonth(); // 0-indexed
            const { dateFrom: from, dateTo: to } = this.getFiscalQuarterRange(today, fiscalStartMonth, false, false);
            dateFrom = from;
            dateTo = to;

        } else if (code == GetDateRangeType.thisQuarterToDate) {
            const fiscalStartMonth = fiscalYearStartDate.getMonth(); // 0-indexed
            const { dateFrom: from, dateTo: to } = this.getFiscalQuarterRange(today, fiscalStartMonth, true, false);
            dateFrom = from;
            dateTo = to;
        } else if (code == GetDateRangeType.thisYear) {
            dateFrom = fiscalYearStartDate;
            dateTo = fiscalYearEndDate
        } else if (code == GetDateRangeType.thisYearToLastMonth) {
            dateFrom = fiscalYearStartDate;
            dateTo = new Date(today.getFullYear(), today.getMonth(), 0);
        } else if (code == GetDateRangeType.thisyeartodate) {
            dateFrom = fiscalYearStartDate;
            dateTo = today;
        } else if (code == GetDateRangeType.lastYear) {
            dateFrom = lastFiscalYearStartDate
            dateTo = lastYearEndDate
        } else if (code == GetDateRangeType.lastYearToDate) {
            dateFrom = lastFiscalYearStartDate;
            dateTo = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
        } else if (code == 'custom' && data?.fromDate && data?.toDate) {
            dateFrom = new Date(data?.fromDate);
            dateTo = new Date(data?.toDate);
        }
        else if (code == 'postingPeriod' && period) {
            dateFrom = new Date(period?.startDate);
            dateTo = new Date(period?.endDate);
        }
        const { fiscalYearStartDate: fiscalYearStartDateNew, fiscalYearEndDate: fiscalYearEndDateNew } = this.calculateIncomeYearRange(this.accountingPeriod, dateFrom);
        // let fiscalYearStartDateNew = fiscalYearStartDate;
        // let fiscalYearEndDateNew = fiscalYearStartDate;
        let dateRangeModel = {
            fromDate: dateFrom,
            toDate: dateTo,
            fiscalYearStartDate: fiscalYearStartDateNew,//.setFullYear(dateFrom.getFullYear()),
            fiscalYearEndDate: fiscalYearEndDateNew,//.setFullYear(dateTo.getFullYear()),
            accountingPeriod: this.accountingPeriod,
        }
        return dateRangeModel;
    }

    calculateYearRange(config: AccountingPeriodWithMonths, today: Date): { fiscalYearStartDate: Date; fiscalYearEndDate: Date } {
        let referenceYear = today.getFullYear();
        let startYear = referenceYear;
        let endYear = referenceYear;

        if (config?.isFinancialYear) {
            if (config?.startMonthId > (today.getMonth() + 1)) {
                startYear = referenceYear - 1;
            }
            else {
                endYear = referenceYear + 1;
            }
        } else {
            return {
                fiscalYearStartDate: new Date(referenceYear, 0, 1),
                fiscalYearEndDate: new Date(referenceYear, 11, 31),
            };
        }
        const fiscalYearStartDate = new Date(startYear, config?.startMonthId == 0 || !(config?.startMonthId) ? 0 : config?.startMonthId - 1, 1);
        const fiscalYearEndDate = new Date(endYear, config?.endMonthId == 0 || !(config?.endMonthId) ? 12 : config?.endMonthId, 0) // 0th of next month = last day of end month
        return { fiscalYearStartDate, fiscalYearEndDate };
    }

    calculateIncomeYearRange(config: AccountingPeriodWithMonths, fromDate: Date): { fiscalYearStartDate: Date; fiscalYearEndDate: Date } {
        let referenceYear = fromDate.getFullYear();
        let startYear = referenceYear;
        let endYear = referenceYear;

        if (config?.isFinancialYear) {
            if (config?.startMonthId > (fromDate.getMonth() + 1)) {
                startYear = referenceYear - 1;
            }
            else {
                endYear = referenceYear + 1;
            }
        } else {
            return {
                fiscalYearStartDate: new Date(referenceYear, 0, 1),
                fiscalYearEndDate: new Date(referenceYear, 11, 31),
            };
        }
        const fiscalYearStartDate = new Date(startYear, config?.startMonthId == 0 || !(config?.startMonthId) ? 0 : config?.startMonthId - 1, 1);
        const fiscalYearEndDate = new Date(endYear, config?.endMonthId == 0 || !(config?.endMonthId) ? 12 : config?.endMonthId, 0) // 0th of next month = last day of end month
        return { fiscalYearStartDate, fiscalYearEndDate };
    }

    isAllowPostingPeriod(): boolean {
        const iSettings = this.utilityService.getISettings();
        return iSettings?.find(obj => obj.code === 'posting')?.enabled ?? false;
    }

    getFiscalQuarterRange(
        today: Date,
        fiscalStartMonth: number,
        toDateIsToday = false,
        isLastQuarter = false
    ): { dateFrom: Date, dateTo: Date } {
        const calendarMonth = today.getMonth();
        const calendarYear = today.getFullYear();

        let fiscalMonth = (calendarMonth - fiscalStartMonth + 12) % 12;
        let fiscalQuarter = Math.floor(fiscalMonth / 3);

        let targetQuarter = fiscalQuarter;
        let targetYear = calendarYear;

        if (isLastQuarter) {
            targetQuarter = fiscalQuarter - 1;
            if (targetQuarter < 0) {
                targetQuarter = 3;
                targetYear -= 1;
            }
        }

        const startMonth = (targetQuarter * 3 + fiscalStartMonth) % 12;
        const endMonth = (startMonth + 3) % 12;

        const startYear = (startMonth < fiscalStartMonth) ? targetYear + 1 : targetYear;
        const endYear = (endMonth <= startMonth) ? startYear + 1 : startYear;

        const dateFrom = new Date(startYear, startMonth, 1);
        const dateTo = toDateIsToday ? today : new Date(endYear, endMonth, 0);

        return { dateFrom, dateTo };
    }
}